import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import MapAutocomplete from "@/components/test/MapAutocompleteTest";
import React from "react";
import useCoordinatesStore from '@/lib/coordinates-store';
import Maps from "@/components/test/MapTest";
import { IconMapPin, IconPin } from "@tabler/icons-react";
import { showOnePlz } from '@/lib/helpers/search-helpers';
import { motion } from "framer-motion"; // Import motion

export default function PreviewComponent({ formData, className }) {
  const { coordinates } = useCoordinatesStore();
  const blurText = (text) => "*****"; // Only for Marktpreis and Werteentwicklung

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 1 }, // Container itself doesn't need to animate opacity here
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08, // Stagger animation of children
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, filter: 'blur(5px)', y: 10 }, // Start blurred, slightly down, and invisible
    visible: {
      opacity: 1,
      filter: 'blur(0px)', // End sharp, at original position, and visible
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 } // Add a subtle spring animation
    },
  };

  return (
    <ScrollArea className={cn("h-[calc(100vh-85px)] xl:pl-32 translate-z-[20rem] ", className)}>
    <div className=" md:shadow-md ">
      {/* <div className="h-2 w-full bg-prime-800"></div> */}
      {/* Map Section */}
      <div className="">
        <div className="w-full overflow-hidden h-64 bg-gray-50 rounded-lg flex items-center justify-center text-gray-500">
          <Maps />
        </div>
      </div>
<div className="px-3">
   {/* Address Section */}
   <div className="mb-6">
    <div className="flex items-center  pt-5 mb-2">
      <IconMapPin className="text-text-100s w-5 h-5 mr-1" />
    <h3 className="md:text-lg text-base font-sans text-text-100">Ihre Adresse</h3>

    </div>
        <p className="text-prime-950 font-medium">{showOnePlz(formData.zip_code)+ " " + (formData.ort || "") + ', ' + (formData.street || "") + " " + (formData.house_number || " -")}</p>
      </div>
      {/* Property Details */}
      <div className="mb-6 min-h-[10rem] bg-gray-50 px-1 py-5 rounded-lg">
        {/* Use motion.div for the grid container to enable staggering */}
        <motion.div
          className="grid font-serif grid-cols-2 gap-x-4 gap-y-2 text-sm" // Adjust gaps if needed
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Typ - Non-animated, or add motion.div if preferred */}
          <div className="flex justify-between items-center py-1 border-b border-gray-200">
            <span className="text-text-100 text-sm font-sans">Typ:</span>
            <p className="text-base font-serif text-prime-950 text-right">{formData.propertyType || "-"}</p>
          </div>

          {/* Conditionally render and animate other items */}
          {formData.rooms && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Zimmer:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.rooms}</p>
            </motion.div>
          )}

          {formData.livingSpace && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Wohnfläche:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.livingSpace} m²</p>
            </motion.div>
          )}

          {formData.plotSize && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Grundfläche:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.plotSize} m²</p>
            </motion.div>
          )}

          {formData.constructionYear && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Baujahr:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.constructionYear}</p>
            </motion.div>
          )}

          {formData.condition && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Kondition:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.condition }</p>
            </motion.div>
          )}

           {formData.balconySize && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Balkongröße:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.balconySize} m²</p>
            </motion.div>
          )}

          {formData.basement && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Unterkellert:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.basement}</p>
            </motion.div>
          )}

          {formData.equipment && (
            <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
              <span className="text-text-100 text-sm font-sans">Ausstattung:</span>
              <p className="text-base font-serif text-prime-950 text-right">{formData.equipment || "-"}</p>
            </motion.div>
          )}

          {formData.parkingSpaces && (
             <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
               <span className="text-text-100 text-sm font-sans">Stellplätze:</span>
               <p className="text-base font-serif text-prime-950 text-right">{formData.parkingSpaces || "-"}</p>
             </motion.div>
          )}

          {formData.garageSpaces && (
             <motion.div variants={itemVariants} className="flex justify-between items-center py-1 border-b border-gray-200">
               <span className="text-text-100 text-sm font-sans">Garagenplätze:</span>
               <p className="text-base font-serif text-prime-950 text-right">{formData.garageSpaces || "-"}</p>
             </motion.div>
          )}

         

        </motion.div>
      </div>

      {/* Market Price Section */}
      <div className="mb-6">
        <h3 className="text-lg text-gray-700 font-serif mb-4">Marktpreis</h3>
        <div className="grid grid-cols-3 gap-4 blur-sm">
          <div className="p-4 bg-gray-50 shadow-sm rounded-lg text-center">
            <p className="font-semibold text-lg">
              {blurText("691,000€")}{" "}
              <span className="text-sm text-green-500">(+3%)</span>
            </p>
            <p className="text-text-100 text-sm">Marktpreis</p>
          </div>
          <div className="p-4 bg-white shadow-sm rounded-lg text-center">
            <p className="font-semibold text-lg">
              {blurText("5,700€")}{" "}
              <span className="text-sm text-green-500">(+3%)</span>
            </p>
            <p className="text-gray-500 text-sm">Preis pro m²</p>
          </div>
          <div className="p-4 bg-white shadow-sm rounded-lg text-center">
            <p className="font-semibold text-lg">{blurText("13 Lauf")}</p>
            <p className="text-gray-500 text-sm">Potenziale</p>
          </div>
        </div>
      </div>

      {/* Value Development Section */}
      <div className="mb-6">
        <h3 className="text-lg font-serif mb-4">Wertentwicklung</h3>
        <div className="blur-sm w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
          {blurText("Graph Placeholder")}
        </div>
      </div>
</div>
   
    </div>
    </ScrollArea>
  );
}
